/**
 * Aus gelesenem Text werden Inventarnummern -- und nur solche, die es gibt.
 *
 * Eine Texterkennung verliest sich. Die Nummern eines Ortsverbands sind
 * fortlaufend, eine falsche Ziffer trifft also leicht ein **anderes echtes
 * Geraet**. Deshalb zaehlt nur, was genau so im Bestand steht, und die
 * Oberflaeche bucht nie von selbst (ADR 0033).
 */

import { INVENTARNUMMER_STELLEN, nurZiffern } from "@thw/core-domain";
import type { Artikel } from "@thw/contracts";

/**
 * Vier Ziffern, ein Trenner, sechs Ziffern. Der Trenner darf fehlen oder als
 * anderer Strich erkannt werden -- das chinesische Modell liest einen
 * Bindestrich gern als „一“. Davor und dahinter keine Ziffer: Aus einer
 * zwoelfstelligen Zahl wird keine Inventarnummer herausgeschnitten.
 */
const MUSTER = /(?<!\d)(\d{4}) ?[-‐‑–—―_.~一]? ?(\d{6})(?!\d)/g;

/** Verwechslungen, die in Zahlen fast immer Ziffern meinen. */
function ziffernAngleichen(text: string): string {
  return text.replace(/[OoQD]/g, "0").replace(/[Il|!]/g, "1");
}

/**
 * Alle Inventarnummern in einer gelesenen Zeile, in der Schreibweise
 * `0141-900639`. Jede Zeile fuer sich: Ueber einen Zeilenumbruch hinweg
 * entsteht sonst aus „DIN VDE 0702“ und einer Pruefnummer darunter eine
 * Nummer, die es nicht gibt.
 */
export function inventarnummernIn(zeile: string): string[] {
  const gefunden: string[] = [];
  for (const m of ziffernAngleichen(zeile).matchAll(MUSTER)) {
    const ziffern = `${m[1] ?? ""}${m[2] ?? ""}`;
    if (ziffern.length !== INVENTARNUMMER_STELLEN) continue;
    const nummer = `${ziffern.slice(0, 4)}-${ziffern.slice(4)}`;
    if (!gefunden.includes(nummer)) gefunden.push(nummer);
  }
  return gefunden;
}

/** Der Artikel mit genau dieser Inventarnummer, sonst `null`. */
export function artikelMitNummer(artikel: Iterable<Artikel>, nummer: string): Artikel | null {
  const gesucht = nurZiffern(nummer);
  for (const a of artikel) if (nurZiffern(a.inventarnummer) === gesucht) return a;
  return null;
}

/**
 * Bestaetigt ist eine Nummer erst, wenn sie **zweimal hintereinander** gleich
 * gelesen wurde. Ein einzelnes verwackeltes Bild reicht nicht.
 */
export const BESTAETIGUNG_NACH = 2;

export class Bestaetigung {
  #letzte: string | null = null;
  #anzahl = 0;

  /** Meldet die Nummer des letzten Versuchs (oder `null`) und sagt, ob sie jetzt steht. */
  melden(nummer: string | null): boolean {
    if (nummer === null) {
      this.#letzte = null;
      this.#anzahl = 0;
      return false;
    }
    this.#anzahl = nummer === this.#letzte ? this.#anzahl + 1 : 1;
    this.#letzte = nummer;
    return this.#anzahl === BESTAETIGUNG_NACH;
  }
}
